import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import {
  Briefcase,
  ArrowLeft,
  Users,
  DollarSign,
  MapPin,
  Calendar
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

const AdminProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem("cdace_token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/admin/hr-projects`, { headers: { Authorization: `Bearer ${token}` } });
        setProjects(response.data.projects);
      } catch (error) {
        if (error.response?.status === 401) navigate("/admin-login");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, navigate]);

  const formatCurrency = (val) => {
    if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
    return `$${val?.toLocaleString() || 0}`;
  };

  if (loading) return <div className="min-h-screen bg-[#0b1c2d] flex items-center justify-center"><div className="text-[#c9a227]">Loading...</div></div>;

  return (
    <div className="min-h-screen bg-[#0b1c2d] lg:ml-64 p-6" data-testid="admin-projects-page">
      <div className="mb-6">
        <Link to="/admin" className="flex items-center gap-2 text-slate-400 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" />Back to Dashboard</Link>
        <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>HR Projects</h1>
        <p className="text-slate-400 mt-1">{projects.length} active projects with HR allocations</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {projects.map((project) => (
          <Card key={project.id} className="glass-card">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-white">{project.project_name}</CardTitle>
                  <p className="text-sm text-slate-400">{project.client_name}</p>
                </div>
                <Badge className={project.status === 'active' ? 'status-green' : 'status-yellow'}>{project.status}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2 text-slate-400"><MapPin className="w-4 h-4" />{project.country}</div>
                <div className="flex items-center gap-2 text-slate-400"><Calendar className="w-4 h-4" />{project.start_date?.split('T')[0]}</div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-800/50 rounded-sm">
                  <p className="text-xs text-slate-500">Project Budget</p>
                  <p className="text-lg font-bold text-[#c9a227]">{formatCurrency(project.project_budget)}</p>
                </div>
                <div className="p-3 bg-slate-800/50 rounded-sm">
                  <p className="text-xs text-slate-500">HR Budget</p>
                  <p className="text-lg font-bold text-purple-400">{formatCurrency(project.hr_budget)}</p>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400 flex items-center gap-1"><Users className="w-4 h-4" />Staff Assigned</span>
                  <span className="text-sm text-white">{project.staff_count || 0}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">Total Allocation</span>
                  <span className={`text-sm font-semibold ${project.allocated_percent > 100 ? 'text-red-400' : 'text-green-400'}`}>
                    {project.allocated_percent || 0}%
                  </span>
                </div>
                <Progress value={Math.min(project.allocated_percent || 0, 100)} className="h-2 bg-slate-700" />
                {project.allocated_percent > 100 && (
                  <p className="text-xs text-red-400 mt-1">Warning: Staff over-allocated</p>
                )}
              </div>

              {project.assignments?.length > 0 && (
                <div className="pt-3 border-t border-slate-700">
                  <p className="text-xs text-slate-500 mb-2">Team Members</p>
                  <div className="flex flex-wrap gap-2">
                    {project.assignments.slice(0, 5).map((a, idx) => (
                      <Badge key={idx} className="bg-slate-700 text-slate-300">{a.user_name?.split(' ')[0]} ({a.allocation_percent}%)</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminProjectsPage;
